import React, { useMemo, useState } from "react";
import moment from "moment";

import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import LocationOffOutlinedIcon from "@mui/icons-material/LocationOffOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import WifiOffOutlinedIcon from "@mui/icons-material/WifiOffOutlined";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";
import TimelineIcon from "@mui/icons-material/Timeline";

/* =========================================================
   LOST ENUMS
   (unchanged)
========================================================= */

const LOST_ENUM = {
  GPS_OFF: "GPS_OFF",
  NORMAL_LOST: "NORMAL_LOST",
  INTERNET_OFF: "INTERNET_OFF",
};

const getLostEnum = (value) => {
  if (!value) {
    return LOST_ENUM.GPS_OFF;
  }

  return value;
};

/* =========================================================
   DURATION HELPERS
   (unchanged)
========================================================= */

const formatDuration = (minutes = 0) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0 && mins > 0) {
    return `${hours}h ${mins}m`;
  }

  if (hours > 0) {
    return `${hours}h`;
  }

  return `${mins}m`;
};

const formatDurationObject = (minutes = 0) => {
  return {
    hours: Math.floor(minutes / 60),
    minutes: minutes % 60,
    duration: formatDuration(minutes),
  };
};

/* =========================================================
   TRANSFORM GPS DATA
   (unchanged — same logic, same data sequence, same return shape)
========================================================= */

export const transformGPSData = (data) => {
  if (!Array.isArray(data)) {
    return {
      data: [],

      totalHours: 0,
      totalMinutes: 0,
      totalDuration: "0m",

      onlineHours: 0,
      onlineMinutes: 0,
      onlineDuration: "0m",

      gpsOffTotalHours: 0,
      gpsOffMinutes: 0,
      gpsOffDuration: "0m",

      airplaneModeTotalHours: 0,
      airplaneModeMinutes: 0,
      airplaneModeDuration: "0m",

      internetOffTotalHours: 0,
      internetOffMinutes: 0,
      internetOffDuration: "0m",

      gpsOffPercentage: 0,
      airplaneModePercentage: 0,
      internetOffPercentage: 0,

      normalLostTotalHours: 0,
      normalLostMinutes: 0,
      normalLostDuration: "0m",
    };
  }
  let totalMinutes = 0;

  let onlineMinutes = 0;

  let gpsOffMinutes = 0;

  let airplaneModeMinutes = 0;

  let internetOffMinutes = 0;

  let normalLostMinutes = 0;

  const result = data
    .map((pair) => {
      if (!pair || pair.length < 2) {
        return null;
      }

      /* =====================================================
         START / END TIME
      ===================================================== */

      const start = moment.utc(pair[0]?.isLocationDetectedTime);

      const end = moment.utc(pair[1]?.isLocationDetectedTime);

      if (!start.isValid() || !end.isValid()) {
        return null;
      }

      /* =====================================================
         DIFFERENCE
      ===================================================== */

      const startForDuration = start.clone().startOf("minute");
      const endForDuration = end.clone().startOf("minute");

      const minutes = endForDuration.diff(startForDuration, "minutes");

      totalMinutes += minutes;

      /* =====================================================
         LOCATION STATUS
      ===================================================== */

      const isLocationDetected = pair[0]?.isLocationDetected === true;

      /*
       * Only use lostEnum when location is NOT detected.
       *
       * If lostEnum is null/undefined,
       * default to GPS_OFF.
       */

      const lostEnum = getLostEnum(
        pair[0]?.gpsLostReason ?? pair[1]?.gpsLostReason,
      );

      let type = "online";

      /* =====================================================
         ONLINE
      ===================================================== */

      if (lostEnum === LOST_ENUM.GPS_OFF) {
        /* =====================================================
         GPS Turned Off
      ===================================================== */
        gpsOffMinutes += minutes;

        type = "gps_off";
      } else if (lostEnum === LOST_ENUM.INTERNET_OFF) {
        /* =====================================================
         INTERNET OFF
      ===================================================== */
        internetOffMinutes += minutes;

        type = "internet_off";
      } else if (lostEnum === LOST_ENUM.NORMAL_LOST) {
        normalLostMinutes += minutes;

        type = "normal_lost";
      } else {
        /* =====================================================
         UNKNOWN ENUM
         
         For safety, treat unknown/null values
         as GPS_OFF.
      ===================================================== */
        gpsOffMinutes += minutes;

        type = "gps_off";
      }

      return {
        startTime: start.local(),

        endTime: end.local(),

        time: `${start.local().format("h:mm A")} - ${end
          .local()
          .format("h:mm A")}`,

        durationMinutes: minutes,

        duration: formatDuration(minutes),

        isLocationDetected,

        lostEnum,

        type,
      };
    })
    .filter(Boolean);

  /* =========================================================
     DURATIONS
  ========================================================= */

  const total = formatDurationObject(totalMinutes);

  const online = formatDurationObject(onlineMinutes);

  const gpsOff = formatDurationObject(gpsOffMinutes);

  const airplaneMode = formatDurationObject(airplaneModeMinutes);

  const internetOff = formatDurationObject(internetOffMinutes);

  const normalLost = formatDurationObject(normalLostMinutes);

  /*
   * Total LOST time
   *
   * Online time is NOT considered lost.
   */

  const totalLostMinutes =
    gpsOffMinutes + normalLostMinutes + internetOffMinutes;

  const totalLost = formatDurationObject(totalLostMinutes);

  /* =========================================================
     PERCENTAGES
     
     Percentages are calculated against TOTAL LOST TIME.
  ========================================================= */

  const gpsOffPercentage = totalLostMinutes
    ? Math.round((gpsOffMinutes / totalLostMinutes) * 100)
    : 0;

  const airplaneModePercentage = totalLostMinutes
    ? Math.round((airplaneModeMinutes / totalLostMinutes) * 100)
    : 0;

  const internetOffPercentage = totalLostMinutes
    ? Math.round((internetOffMinutes / totalLostMinutes) * 100)
    : 0;

  const normalLostPercentage = totalLostMinutes
    ? Math.round((normalLostMinutes / totalLostMinutes) * 100)
    : 0;

  return {
    data: result,

    /* =====================================================
       TOTAL TRACKED TIME
    ===================================================== */

    trackedTotalHours: total.hours,

    trackedTotalMinutes: total.minutes,

    trackedTotalDuration: total.duration,

    /* =====================================================
       TOTAL LOST TIME
       
       This is what the old "Total Lost" displayed.
    ===================================================== */

    totalHours: totalLost.hours,

    totalMinutes: totalLost.minutes,

    totalDuration: totalLost.duration,

    /* =====================================================
       ONLINE
    ===================================================== */

    onlineHours: online.hours,

    onlineMinutes: online.minutes,

    onlineDuration: online.duration,

    /* =====================================================
       GPS Turned Off
    ===================================================== */

    gpsOffTotalHours: gpsOff.hours,

    gpsOffMinutes: gpsOff.minutes,

    gpsOffDuration: gpsOff.duration,

    /* =====================================================
       AIRPLANE MODE
    ===================================================== */

    airplaneModeTotalHours: airplaneMode.hours,

    airplaneModeMinutes: airplaneMode.minutes,

    airplaneModeDuration: airplaneMode.duration,

    /* ==========================================
     * NORMAL LOST / GPS Lost
     * ==========================================
     */

    normalLostTotalHours: normalLost.hours,

    normalLostMinutes: normalLost.minutes,

    normalLostDuration: normalLost.duration,

    /* =====================================================
       INTERNET OFF
    ===================================================== */

    internetOffTotalHours: internetOff.hours,

    internetOffMinutes: internetOff.minutes,

    internetOffDuration: internetOff.duration,

    /* =====================================================
       PERCENTAGES
    ===================================================== */

    gpsOffPercentage,

    airplaneModePercentage,

    internetOffPercentage,

    normalLostPercentage,
  };
};

/* =========================================================
   EVENT VISUAL MAPPING
   Pulled out of the old TimelineEvent component so the exact
   same color / icon / label rules are reused by the new UI.
   (logic unchanged, just lifted into a helper)
========================================================= */

const getEventVisual = (type) => {
  // Default (matches the original component's fallback exactly:
  // anything that isn't airplane_mode / internet_off / normal_lost —
  // including "gps_off" and any unrecognized type — uses this).
  let color = "#ED6C02";
  let backgroundColor = "rgba(237, 108, 2, 0.08)";
  let label = "GPS Turned Off";
  let icon = <LocationOffOutlinedIcon sx={{ fontSize: 16 }} />;

  if (type === "airplane_mode") {
    color = "#0288D1";
    backgroundColor = "rgba(2, 136, 209, 0.08)";
    label = "Airplane Mode";
    icon = <FlightTakeoffOutlinedIcon sx={{ fontSize: 16 }} />;
  } else if (type === "internet_off") {
    color = "#D32F2F";
    backgroundColor = "rgba(211, 47, 47, 0.08)";
    label = "Internet Off";
    icon = <WifiOffOutlinedIcon sx={{ fontSize: 16 }} />;
  } else if (type === "normal_lost") {
    color = "#7B1FA2";
    backgroundColor = "rgba(123, 31, 162, 0.08)";
    label = "GPS Lost";
    icon = <WifiOutlinedIcon sx={{ fontSize: 16 }} />;
  }

  return { color, backgroundColor, label, icon };
};

/* =========================================================
   MAIN COMPONENT
   Responsive: stacks vertically on mobile, feeds device-aware
   sizing defaults into the curved timeline (any `layout` prop
   you pass still overrides these per-key).
========================================================= */

const GpsLocationStatus = ({ data = [], time = [], layout }) => {
  const [timelineOpen, setTimelineOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.down("md")); // < 900px

  const summary = useMemo(() => transformGPSData(data), [data]);

  // Sensible sizing per device. Anything the caller passes in `layout`
  // still wins — this only fills in defaults for whatever they didn't set.
  const deviceLayout = useMemo(() => {
    if (isMobile) {
      return {
        itemsPerRow: 2,
        rowHeight: 210,
        colWidth: 140,
        nodeRadius: 18,
        timeGap: 22,
        chipGap: 22,
        padding: 24,
      };
    }
    if (isTablet) {
      return {
        itemsPerRow: 3,
        rowHeight: 200,
        colWidth: 170,
        nodeRadius: 20,
        timeGap: 26,
        chipGap: 26,
        padding: 32,
      };
    }
    return {}; // desktop uses DEFAULT_LAYOUT as-is
  }, [isMobile, isTablet]);

  const effectiveLayout = { ...deviceLayout, ...layout };

  return (
    <>
      {/* =====================================================
          COMPACT GPS SECTION
      ===================================================== */}

      <Box
        sx={{
          backgroundColor: "#a7dcdf",

          borderRadius: 1,

          px: { xs: 1, sm: 1 },

          py: { xs: 1, sm: 0.7 },

          display: "flex",

          flexDirection: { xs: "column", sm: "row" },

          alignItems: { xs: "stretch", sm: "center" },

          gap: { xs: 1, sm: 1.5 },

          width: "100%",

          boxSizing: "border-box",
        }}
      >
        {/* ===================================================
            GPS LABEL
        =================================================== */}

        <Box
          sx={{
            width: { xs: "100%", sm: 150 },

            minWidth: { xs: "auto", sm: 150 },

            minHeight: { xs: 30, sm: 60 },

            display: "flex",

            flexDirection: { xs: "row", sm: "column" },

            alignItems: "center",

            justifyContent: "center",

            gap: { xs: 0.75, sm: 0 },

            border: "1px solid #1976d2",

            borderRadius: 1,

            backgroundColor: "rgba(255,255,255,0.08)",
          }}
        >
          <Typography variant="body2" color="primary" fontWeight={600}>
            GPS
          </Typography>

          <Typography variant="caption" color="primary">
            Location Status
          </Typography>
        </Box>

        {/* ===================================================
            SUMMARY
        =================================================== */}

        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          spacing={{ xs: 1.5, sm: 2 }}
          rowGap={1}
          sx={{
            flex: 1,

            minWidth: 0,
          }}
        >
          {/* TOTAL LOST */}

          <SummaryItem label="Total Lost" value={summary.totalDuration} />

          <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

          {/* GPS Turned Off */}

          <SummaryItem
            label="GPS Turned Off"
            value={summary.gpsOffDuration}
            color="warning.main"
          />

          <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

          {/* AIRPLANE */}

          {/* <SummaryItem
            label="Airplane Mode"
            value={summary.airplaneModeDuration}
            color="info.main"
          /> */}

          {/* GPS Lost */}

          <SummaryItem
            label="GPS Lost"
            value={summary.normalLostDuration}
            color="secondary.main"
          />

          <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

          {/* INTERNET */}

          <SummaryItem
            label="Internet Off"
            value={summary.internetOffDuration}
            color="error.main"
          />
        </Stack>

        {/* ===================================================
            VIEW TIMELINE
        =================================================== */}

        <Button
          variant="outlined"
          size="small"
          fullWidth={isMobile}
          startIcon={<TimelineIcon />}
          onClick={() => setTimelineOpen(true)}
          sx={{
            backgroundColor: "#fff",

            borderColor: "primary.main",

            whiteSpace: "nowrap",

            minWidth: { xs: "auto", sm: 135 },

            "&:hover": {
              backgroundColor: "#f5f9ff",
            },
          }}
        >
          View Timeline
        </Button>
      </Box>

      {/* =====================================================
          TIMELINE POPUP (new UI)
      ===================================================== */}

      <GpsTimelineDialog
        open={timelineOpen}
        onClose={() => setTimelineOpen(false)}
        timelineData={summary.data}
        summary={summary}
        time={time}
        layout={effectiveLayout}
        fullScreen={isMobile}
      />
    </>
  );
};

/* =========================================================
   SUMMARY ITEM
   (unchanged)
========================================================= */

const SummaryItem = ({ label, value, color = "text.primary" }) => {
  return (
    <Box
      sx={{
        minWidth: 90,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        lineHeight={1.2}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={700}
        color={color}
        sx={{
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

/* =========================================================
   STAT CARD (colored background, white text — your style)
   Now responsive: 2-up on mobile, auto-flow from tablet up.
========================================================= */

const StatCard = ({ label, value, color = "#1976d2" }) => (
  <Box
    sx={{
      flex: { xs: "1 1 calc(50% - 8px)", sm: "1 1 120px" },
      minWidth: { xs: 0, sm: 110 },
      borderRadius: 2,
      px: 1.5,
      py: 1,
      backgroundColor: color,
      border: "1px solid",
      borderColor: "divider",
      boxSizing: "border-box",
    }}
  >
    <Typography variant="caption" color="white" display="block">
      {label}
    </Typography>
    <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#fff" }}>
      {value}
    </Typography>
  </Box>
);

/* =========================================================
   TIMELINE DIALOG — NEW UI
   Same props (open, onClose, timelineData, summary, time),
   same data being displayed — a fresh visual design plus a
   `fullScreen` prop for mobile (passed down automatically
   from <GpsLocationStatus> as isMobile).
========================================================= */

const GpsTimelineDialog = ({
  open,

  onClose,

  timelineData = [],

  summary,

  time = [],

  layout,

  fullScreen = false,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          width: "100%",
          maxHeight: fullScreen ? "100%" : "88vh",
          overflow: "hidden",
        },
      }}
    >
      {/* ===================================================
          HEADER
      =================================================== */}

      <DialogTitle
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          background: "linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)",
          color: "#fff",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.18)",
                width: { xs: 32, sm: 36 },
                height: { xs: 32, sm: 36 },
                flexShrink: 0,
              }}
            >
              <AccessTimeIcon fontSize="small" />
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                GPS Location Timeline
              </Typography>

              <Typography
                variant="caption"
                noWrap
                sx={{ color: "rgba(255,255,255,0.8)", display: { xs: "none", sm: "block" } }}
              >
                Complete location detection history
              </Typography>
            </Box>
          </Stack>

          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: "#fff", flexShrink: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* ===================================================
          SUMMARY STAT CARDS
      =================================================== */}

      <Box
        sx={{
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1.25, sm: 1.75 },
          backgroundColor: "#f4f7fb",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          gap={{ xs: 1, sm: 1.25 }}
        >
          <StatCard
            label="Total Lost Time"
            value={summary?.totalDuration || "0m"}
            color="#1976d2"
          />

          <StatCard
            label="GPS Turned Off"
            value={summary?.gpsOffDuration || "0m"}
            color="#ED6C02"
          />

          <StatCard
            label="GPS Lost"
            value={summary?.normalLostDuration || "0m"}
            color="#7B1FA2"
          />

          <StatCard
            label="Internet Off"
            value={summary?.internetOffDuration || "0m"}
            color="#D32F2F"
          />

          <StatCard label="Events" value={timelineData.length} color="#334155" />
        </Stack>
      </Box>

      {/* ===================================================
          TIMELINE CONTENT
      =================================================== */}

      <DialogContent
        sx={{
          px: { xs: 1, sm: 3 },
          py: { xs: 2, sm: 3 },
          overflowY: "auto",
          backgroundColor: "#fbfcfe",
        }}
      >
        {timelineData.length > 0 ? (
          <GpsCurvedTimeline timelineData={timelineData} time={time} layout={layout} />
        ) : (
          <Box
            sx={{
              py: 6,
              textAlign: "center",
            }}
          >
            <Typography color="text.secondary">
              No GPS timeline data available
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* =========================================================
   CURVED SNAKE TIMELINE (new UI)

   Renders START -> every event in `timelineData` -> END as
   nodes along a snaking connector: straight lines within a
   row, true 180° semicircle arcs at each row turn. Layout,
   row-wrapping and the SVG canvas size are all computed from
   the data length, so it works the same whether there are
   2 events or 50.

   SIZE & SPACING: fully controllable via the `layout` prop,
   which flows all the way down from <GpsLocationStatus>. See
   DEFAULT_LAYOUT below for every available knob, e.g.:

   <GpsLocationStatus
     data={data}
     time={time}
     layout={{ rowHeight: 240, colWidth: 220, nodeRadius: 28, itemsPerRow: 3 }}
   />

   MOBILE: <GpsLocationStatus> already computes device-aware
   defaults (see deviceLayout above) and merges them in before
   this ever runs, so no extra work is needed here — it just
   receives whatever `layout` it's handed.

   IMPORTANT: this only changes how the same event objects
   (startTime, endTime, durationMinutes, type, ...) are
   displayed — no event data is recomputed or reordered here.
========================================================= */

// Every size/spacing knob for the curved timeline lives here.
// Override any of these by passing a `layout` prop, e.g.
// <GpsLocationStatus layout={{ rowHeight: 240, nodeRadius: 28 }} />
const DEFAULT_LAYOUT = {
  itemsPerRow: null, // null = auto (fits everything in one row up to 4, then wraps)
  rowHeight: 190, // vertical distance between rows
  colWidth: 200, // horizontal distance between nodes in a row
  startX: 90, // x position of the very first node
  row0Y: 90, // y position of the first row
  nodeRadius: 22, // radius of the colored circular marker
  timeGap: 28, // gap between the marker and the time label above it
  chipGap: 28, // gap between the marker and the chip/label below it
  padding: 40, // breathing room around the whole diagram
};

/** Fills in any layout values the caller didn't provide. */
const mergeLayout = (layout) => ({ ...DEFAULT_LAYOUT, ...layout });

/** True semicircle turn between two rows — diameter equals the
 *  vertical gap between the rows, bulging left or right. */
function makeTurn(x1, y1, x2, y2, side) {
  const r = Math.abs(y2 - y1) / 2;
  const cornerX = x1;
  const sweep = side === "right" ? 1 : 0;
  const bulgeX = side === "right" ? cornerX + r : cornerX - r;

  const d =
    `L ${cornerX},${y1} ` +
    `A ${r} ${r} 0 0 ${sweep} ${cornerX},${y2} ` +
    `L ${x2},${y2}`;

  return { d, bulgeX };
}

function useCurvedLayout(nodes, layout) {
  return useMemo(() => {
    if (!nodes.length) {
      return { positioned: [], path: "", viewBox: { minX: 0, minY: 0, width: 1, height: 1 } };
    }

    const { itemsPerRow, rowHeight, colWidth, startX, row0Y, nodeRadius, timeGap, chipGap, padding } = layout;
    const perRow = itemsPerRow || Math.min(4, Math.max(1, nodes.length));

    const positioned = nodes.map((node, i) => {
      const rowIndex = Math.floor(i / perRow);
      const idxInRow = i % perRow;
      const reversed = rowIndex % 2 === 1;
      const col = reversed ? perRow - 1 - idxInRow : idxInRow;
      return {
        ...node,
        cx: startX + col * colWidth,
        cy: row0Y + rowIndex * rowHeight,
        rowIndex,
      };
    });

    let d = `M ${positioned[0].cx},${positioned[0].cy}`;
    const bulgeXs = [];

    positioned.forEach((node, i) => {
      if (i === 0) return;
      const prev = positioned[i - 1];
      if (prev.cy === node.cy) {
        d += ` L ${node.cx},${node.cy}`;
      } else {
        const side = prev.rowIndex % 2 === 0 ? "right" : "left";
        const turn = makeTurn(prev.cx, prev.cy, node.cx, node.cy, side);
        d += ` ${turn.d}`;
        bulgeXs.push(turn.bulgeX);
      }
    });

    const xs = [
      ...positioned.map((n) => n.cx - nodeRadius),
      ...positioned.map((n) => n.cx + nodeRadius),
      ...bulgeXs,
    ];
    const ys = [
      ...positioned.map((n) => n.cy - nodeRadius - timeGap - 24), // room for the time label above
      ...positioned.map((n) => n.cy + nodeRadius + chipGap + 20), // room for the status chip below
    ];

    const minX = Math.min(...xs) - padding;
    const maxX = Math.max(...xs) + padding;
    const minY = Math.min(...ys) - padding;
    const maxY = Math.max(...ys) + padding;

    return {
      positioned,
      path: d,
      viewBox: { minX, minY, width: maxX - minX, height: maxY - minY },
    };
  }, [nodes, layout]);
}

const GpsCurvedTimeline = ({ timelineData = [], time = [], layout: layoutProp }) => {
  const layout = mergeLayout(layoutProp);
  const hasData = timelineData.length > 0 && time.length > 0;

  const firstEvent = hasData ? time[0] : null;
  const lastEvent = hasData ? time[time.length - 1] : null;

  // Build one flat node list: START, every event, END —
  // same data, just laid out along the curve instead of a list.
  // (Always called — never skipped by an early return — so hook
  // order stays consistent across renders.)
  const nodes = useMemo(() => {
    if (!hasData) return [];

    const startNode = {
      kind: "endpoint",
      label: "START",
      time: firstEvent.startTime,
      color: "#2e7d32",
      icon: <PlayCircleOutlineIcon sx={{ fontSize: layout.nodeRadius * 0.9 }} />,
    };

    const eventNodes = timelineData.map((event, index) => {
      const visual = getEventVisual(event.type);
      return {
        kind: "event",
        key: `${event.startTime.valueOf()}-${index}`,
        event,
        ...visual,
      };
    });

    const endNode = {
      kind: "endpoint",
      label: "END",
      time: lastEvent.endTime,
      color: "#2e7d32",
      icon: <StopCircleOutlinedIcon sx={{ fontSize: layout.nodeRadius * 0.9 }} />,
    };

    return [startNode, ...eventNodes, endNode];
  }, [hasData, timelineData, firstEvent, lastEvent, layout.nodeRadius]);

  const { positioned, path, viewBox } = useCurvedLayout(nodes, layout);
  const { nodeRadius, timeGap, chipGap } = layout;

  // Safe to bail out now — both hooks above have already run,
  // so hook order stays identical on every render.
  if (!hasData) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: `${viewBox.width} / ${viewBox.height}`,
      }}
    >
      {/* connector line */}
      <Box
        component="svg"
        viewBox={`${viewBox.minX} ${viewBox.minY} ${viewBox.width} ${viewBox.height}`}
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      >
        <path
          d={path}
          fill="none"
          stroke="#c9d3de"
          strokeWidth={2.5}
          strokeLinecap="round"
        />
      </Box>

      {/* nodes: time label, colored marker, and chip/endpoint label */}
      {positioned.map((node, i) => {
        const leftPct = ((node.cx - viewBox.minX) / viewBox.width) * 100;
        const topPct = ((node.cy - viewBox.minY) / viewBox.height) * 100;
        const isEndpoint = node.kind === "endpoint";

        return (
          <React.Fragment key={node.key || i}>
            {/* time label above the marker */}
            <Box
              sx={{
                position: "absolute",
                left: `${leftPct}%`,
                top: `${topPct}%`,
                transform: `translate(-50%, -100%) translateY(-${nodeRadius + timeGap}px)`,
                whiteSpace: "nowrap",
              }}
            >
              {isEndpoint ? (
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: node.color }}
                >
                  {node.time?.format("h:mm A")}
                </Typography>
              ) : (
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {node.event.startTime.format("h:mm A")} –{" "}
                  {node.event.endTime.format("h:mm A")}
                </Typography>
              )}
            </Box>

            {/* colored marker */}
            <Box
              sx={{
                position: "absolute",
                left: `${leftPct}%`,
                top: `${topPct}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <Avatar
                sx={{
                  width: nodeRadius * 2,
                  height: nodeRadius * 2,
                  bgcolor: node.color,
                  border: "3px solid #fff",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
                }}
              >
                {node.icon}
              </Avatar>
            </Box>

            {/* label below the marker */}
            <Box
              sx={{
                position: "absolute",
                left: `${leftPct}%`,
                top: `${topPct}%`,
                transform: `translate(-50%, 0) translateY(${nodeRadius + chipGap}px)`,
                whiteSpace: "nowrap",
              }}
            >
              {isEndpoint ? (
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: node.color }}
                >
                  {node.label}
                </Typography>
              ) : (
                <>
                  {/* <Chip
                    size="small"
                    icon={node.icon}
                    label={node.label}
                    sx={{
                      height: 26,
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      color: node.color,
                      backgroundColor: node.backgroundColor,
                      "& .MuiChip-icon": {
                        color: node.color,
                        fontSize: 15,
                      },
                    }}
                  /> */}
                  {/* <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    textAlign="center"
                    sx={{ mt: 0.4 }}
                  >
                    ({formatDuration(node.event.durationMinutes)})
                  </Typography> */}
                </>
              )}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default GpsLocationStatus;